import React, { useState } from 'react';

interface NewsletterSignupProps {
    variant?: 'inline' | 'card' | 'banner';
    className?: string;
}

/**
 * NewsletterSignup — Email list subscription component using Resend Audience API.
 * The backend route POST /api/newsletter/subscribe handles the actual subscription.
 *
 * Usage:
 *   <NewsletterSignup />                        — defaults to 'inline' variant
 *   <NewsletterSignup variant="card" />         — card with description
 *   <NewsletterSignup variant="banner" />       — full-width landing page banner
 */
const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ variant = 'inline', className = '' }) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;

        setStatus('loading');
        setMessage('');

        try {
            const apiBase = import.meta.env.VITE_API_URL || 'https://techunity-ghbh.onrender.com/api';
            const res = await fetch(`${apiBase}/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Subscription failed. Please try again.');
            }

            setStatus('success');
            setMessage(data.message || 'You\'re on the list! We\'ll keep you updated.');
            setEmail('');
        } catch (err: any) {
            setStatus('error');
            setMessage(err.message || 'Something went wrong. Please try again.');
        }
    };

    if (variant === 'banner') {
        return (
            <section className={`bg-black/40 border border-premium-gold/20 rounded-2xl p-12 text-center ${className}`}>
                <div className="max-w-xl mx-auto">
                    <div className="text-4xl mb-4">📬</div>
                    <h2 className="text-3xl font-serif font-bold text-premium-gold mb-3">Stay in the Loop</h2>
                    <p className="text-white/70 mb-8 leading-relaxed">
                        Get exclusive updates on new features, freelance opportunities, and Academy courses
                        delivered straight to your inbox. No spam, ever.
                    </p>
                    <_Form email={email} setEmail={setEmail} status={status} message={message} onSubmit={handleSubmit} />
                </div>
            </section>
        );
    }

    if (variant === 'card') {
        return (
            <div className={`bg-black/30 border border-premium-gold/20 rounded-xl p-8 ${className}`}>
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">📬</span>
                    <div>
                        <h3 className="font-bold text-white text-lg">Newsletter</h3>
                        <p className="text-white/50 text-sm">Updates, opportunities, & more</p>
                    </div>
                </div>
                <_Form email={email} setEmail={setEmail} status={status} message={message} onSubmit={handleSubmit} />
            </div>
        );
    }

    // inline (default)
    return (
        <div className={className}>
            <_Form email={email} setEmail={setEmail} status={status} message={message} onSubmit={handleSubmit} />
        </div>
    );
};

interface FormProps {
    email: string;
    setEmail: (v: string) => void;
    status: 'idle' | 'loading' | 'success' | 'error';
    message: string;
    onSubmit: (e: React.FormEvent) => void;
}

const _Form: React.FC<FormProps> = ({ email, setEmail, status, message, onSubmit }) => {
    if (status === 'success') {
        return (
            <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3">
                <span className="text-green-400 text-xl">✅</span>
                <p className="text-green-400 text-sm font-medium">{message}</p>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} className="space-y-3">
            <div className="flex gap-2">
                <input
                    id="newsletter-email"
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === 'loading'}
                    className="flex-1 bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-premium-gold transition-colors text-sm disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="bg-premium-gold text-premium-wood-dark font-bold px-5 py-3 rounded-lg hover:opacity-90 transition-opacity text-sm shrink-0 disabled:opacity-50"
                >
                    {status === 'loading' ? '...' : 'Subscribe'}
                </button>
            </div>
            {status === 'error' && message && (
                <p className="text-red-400 text-xs">{message}</p>
            )}
            <p className="text-white/30 text-xs">No spam. Unsubscribe anytime.</p>
        </form>
    );
};

export default NewsletterSignup;
